/**
 * Bewertung hinzufügen (Tab „Bewertungen"), gleiche Komponente für PB und BK.
 *
 * Bewertende Person: verknüpftes Profil aus dem System (Picker) ODER ein frei
 * eingegebener Name ohne Profilbezug.
 *
 * PB bewerten (ratedRole 'PB'): Profil = BK. Kriterien wie die BK-Bewertung
 * eines PB in CRM 1.0 (ratings_bk): Wohnqualität, Charakter, Pflegeaufwand,
 * Umfeld, Freizeit.
 *
 * BK bewerten (ratedRole 'BK'): Profil = Kunde (PB), Kriterien Erscheinungsbild,
 * Aufgeschlossenheit, Zusammenarbeit, Kompetenz, Haushalt, Sprachkenntnisse
 * (Tabelle ratings, siehe ratingCriteria.ts).
 *
 * Gespeichert über POST /v2/admin/users/{id}/ratings.
 *
 * Je 1–5 Sterne; die Gesamtnote berechnet das Backend.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, EditDrawer } from '../../../components/ui';
import { BRAND, BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../../lib/theme';
import { v1Users, type V2RatingCreate } from '../../../lib/v1';
import { toLocalDatetimeInput } from '../../../lib/localDate';
import { BkPicker } from './BetreuungenTab/BkPicker';
import { BK_RATING_CRITERIA, PB_RATING_CRITERIA } from './ratingCriteria';

const emptyScores = (keys: readonly string[]) => Object.fromEntries(keys.map(k => [k, 0])) as Record<string, number>;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Bewertete Person (PB oder BK, siehe ratedRole). */
  pbUserId: number;
  /** Rolle der bewerteten Person — 'PB' (Default) oder 'BK'. */
  ratedRole?: 'PB' | 'BK';
  dark: boolean;
  onCreated: () => void;
}

export function RatingCreateDrawer({ open, onClose, pbUserId, ratedRole = 'PB', dark, onCreated }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const ratesBk = ratedRole === 'BK';
  const criteria = ratesBk ? BK_RATING_CRITERIA : PB_RATING_CRITERIA;
  const criteriaKeys = criteria.map(c => c.key);
  const [mode, setMode] = useState<'bk' | 'name'>('bk');
  // Ausgewählte bewertende Person aus dem System (BK bzw. beim BK-Bewerten der PB).
  const [bkId, setBkId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [scores, setScores] = useState<Record<string, number>>(() => emptyScores(criteriaKeys));
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bei jedem Öffnen frisches Formular.
  useEffect(() => {
    if (!open) return;
    setMode('bk'); setBkId(null); setName(''); setText(''); setError(null);
    setScores(emptyScores(criteriaKeys));
  }, [open, ratedRole]);

  const raterOk = mode === 'bk' ? bkId != null : name.trim() !== '';
  const scoresOk = criteriaKeys.every(k => (scores[k] ?? 0) >= 1);
  const canSave = raterOk && scoresOk && !saving;

  const save = async () => {
    if (!raterOk) { setError(tr(ratesBk ? 'pflege.ratings.create.error_rater_pb' : 'pflege.ratings.create.error_rater')); return; }
    if (!scoresOk) { setError(tr('pflege.ratings.create.error_scores')); return; }
    setSaving(true);
    setError(null);
    const payload = {
      ...(mode === 'bk' ? { von_user_id: bkId } : { von_name: name.trim() }),
      ...Object.fromEntries(criteria.map(c => [c.field, scores[c.key]])),
      rating_text: text.trim() || null,
      // Bewertungsdatum: aktuelle Ortszeit.
      created_at: `${toLocalDatetimeInput(new Date()).replace('T', ' ')}:00`,
    } as V2RatingCreate;
    try {
      await v1Users.createRating(pbUserId, payload);
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: t.muted, marginBottom: 6, display: 'block' };
  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: `1px solid ${t.border}`, borderRadius: 8,
    padding: '8px 10px', fontSize: 13, background: t.inputBg, color: t.text, fontFamily: 'Outfit, sans-serif',
  };

  return (
    <EditDrawer open={open} onClose={onClose} title={tr('pflege.ratings.create.title')} dark={dark}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Bewertende Person: BK oder Freitext */}
        <div>
          <span style={label}>{tr('pflege.ratings.create.rater')}</span>
          <div role="radiogroup" style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {(['bk', 'name'] as const).map(m => {
              const on = m === mode;
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => { setMode(m); setError(null); }}
                  style={{
                    flex: 1, border: `1px solid ${on ? BRAND : t.border}`, borderRadius: 8, padding: '7px 10px',
                    background: on ? BRAND_LIGHT : t.card, color: on ? BRAND_DARK : t.text,
                    fontSize: 12.5, fontWeight: on ? 600 : 500, cursor: 'pointer', fontFamily: 'Outfit, sans-serif',
                  }}
                >{tr(m === 'bk' && ratesBk ? 'pflege.ratings.create.mode_pb' : `pflege.ratings.create.mode_${m}`)}</button>
              );
            })}
          </div>
          {mode === 'bk' ? (
            <BkPicker dark={dark} pbUserId={pbUserId} role={ratesBk ? 'PB' : 'BK'} value={bkId} onChange={id => { setBkId(id); setError(null); }} />
          ) : (
            <input
              aria-label={tr('pflege.ratings.create.name')}
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={150}
              style={input}
            />
          )}
        </div>

        {/* Kriterien, je 1–5 Sterne */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {criteriaKeys.map(c => (
            <div key={c} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ fontSize: 13, color: t.text }}>{tr(`pflege.ratings.create.criteria.${c}`)}</span>
              <StarInput
                label={tr(`pflege.ratings.create.criteria.${c}`)}
                value={scores[c] ?? 0}
                onChange={v => setScores(s => ({ ...s, [c]: v }))}
                dark={dark}
              />
            </div>
          ))}
        </div>

        <div>
          <span style={label}>{tr('pflege.ratings.create.text')}</span>
          <textarea
            aria-label={tr('pflege.ratings.create.text')}
            value={text}
            onChange={e => setText(e.target.value)}
            rows={3}
            style={{ ...input, resize: 'vertical' }}
          />
        </div>

        {error && <div role="alert" style={{ fontSize: 12, color: '#b42318' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" size="sm" dark={dark} onClick={onClose}>{tr('common.action.cancel')}</Button>
          <Button variant="primary" size="sm" dark={dark} onClick={() => void save()} disabled={!canSave}>
            {saving ? tr('pflege.edit.saving') : tr('pflege.ratings.create.save')}
          </Button>
        </div>
      </div>
    </EditDrawer>
  );
}

/** 1–5 Sterne als Radio-Gruppe (Tastatur + Screenreader). */
function StarInput({ label, value, onChange, dark }: { label: string; value: number; onChange: (v: number) => void; dark: boolean }) {
  const t = getThemeColors(dark);
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${label}: ${n}`}
          onClick={() => onChange(n)}
          style={{
            background: 'none', border: 'none', padding: 2, cursor: 'pointer',
            fontSize: 20, lineHeight: 1, color: n <= value ? '#f5a623' : t.border,
          }}
        >★</button>
      ))}
    </div>
  );
}
